import { ReactElement, useContext, useEffect, useState } from 'react';
import * as tsClient from '@tsClient';
import useNoodleApi from '@hooks/useNoodleApi';
import { useToast } from '@hooks';
import teamsContext from '@providers/Teams/TeamsContext';
import { ToastTypeVariants } from '@context/ToastContext';
import getCreatorProfile from '@tsClient/creators/getCreatorProfile';
import Modal from '@components/Modal';
import TextInput from '@components/FormFields/TextInput';
import EmailInput from '@components/FormFields/EmailInput';
import PhoneInput from '@components/DesignLibrary/Atoms/PhoneInput';
import Button from '@/components/DesignLibrary/Button';
import Spacer from '@components/Spacer';
import s from './CreateContactModal.module.scss';

type CreateContactModalProps = {
  onClose: () => void;
};

const CreateContactModal = ({ onClose }: CreateContactModalProps): ReactElement => {
  const addToast = useToast();
  const { creatorId } = useContext(teamsContext);

  const [name, setName] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);

  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);

  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneNumberError, setPhoneNumberError] = useState<string | null>(null);

  const [hasAttemptedToSubmit, setHasAttemptedToSubmit] = useState(false);

  const { data: fetchedCreator, getData: getCreatorProfileFn } = useNoodleApi(getCreatorProfile, {
    toastErrorMessage: () => [ToastTypeVariants.ERROR, 'Failed to fetch creator profile. Please try again.'],
    toastOnError: true,
  });

  const { getData: findOrCreatePersonFn, fetchingState: isFindingOrCreatingPersonState } = useNoodleApi(tsClient.findOrCreatePerson, {
    toastErrorMessage: error => [
      useToast.ToastTypeVariants.ERROR,
      error?.errors?.[0] || 'There was an error creating the contact. Please try again.',
    ],
    toastOnError: true,
    toastSuccessMessage: () => [useToast.ToastTypeVariants.SUCCESS, 'Contact created successfully!'],
  });

  const { getData: isTeamMemberFn, fetchingState: isCheckingTeamMember } = useNoodleApi(tsClient.creators.isTeamMember, {
    toastErrorMessage: error => [ToastTypeVariants.ERROR, error?.errors?.[0] || 'Error checking team membership. Please try again.'],
    toastOnError: true,
  });

  const handleSubmit = async (): Promise<void> => {
    if (!creatorId || !fetchedCreator) {
      addToast(ToastTypeVariants.ERROR, 'Unable to determine creator team. Please try refreshing the page or contact support.');
      return;
    }

    setHasAttemptedToSubmit(true);

    if (emailError || nameError || phoneNumberError) {
      return;
    }

    const { data: teamMemberResponse } = await isTeamMemberFn({
      creatorId,
      email,
      phone: phoneNumber,
    });

    if (teamMemberResponse?.isTeamMember) {
      addToast(ToastTypeVariants.ERROR, 'Contact is a member of your team. Please use a different email/phone number that is not already in use.');
      return;
    }

    await findOrCreatePersonFn({
      contactPhoneNumber: phoneNumber,
      creatorSlug: fetchedCreator.slug,
      identifier: email,
      name,
      shouldSendAccountCreatedEmail: true,
    });

    onClose();
  };

  useEffect(() => {
    if (creatorId) {
      getCreatorProfileFn({ creatorId });
    }
  }, [creatorId, getCreatorProfileFn]);

  return (
    <Modal title="Create new contact" onClose={onClose}>
      <div className={s.createCustomerForm}>
        <TextInput
          id="name"
          value={name}
          onChange={newName => setName(newName)}
          label="Name"
          placeholder="Name"
          required
          isTouched={hasAttemptedToSubmit}
          onError={setNameError}
          error={nameError}
        />

        <EmailInput
          id="email"
          value={email}
          onChange={newEmail => setEmail(newEmail)}
          label="Email"
          placeholder="Email"
          required
          isTouched={hasAttemptedToSubmit}
          onError={setEmailError}
          error={emailError}
        />

        <PhoneInput
          id="phone"
          value={phoneNumber}
          onChange={setPhoneNumber}
          label="Phone"
          isTouched={hasAttemptedToSubmit}
          onError={setPhoneNumberError}
          error={phoneNumberError}
          notInQuestionnaire
        />

        <Spacer size={8} />

        <Button
          loading={isCheckingTeamMember.isFetching || isFindingOrCreatingPersonState.isFetching}
          variant="primary"
          size="md"
          fullWidth
          onClick={handleSubmit}
        >
          Save
        </Button>
      </div>
    </Modal>
  );
};

export default CreateContactModal;
